import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { memberPhotoSizedUrl } from '../../../kamo-branding/logoVariants';
import { IconUser } from '../../icons/svg';
import { getParticipantById } from '../../participants/functions';
import { getAvatarColor, getInitials, isCORSAvatarURL } from '../functions';
import { StatelessAvatar } from './';
export const DEFAULT_SIZE = 65;
/**
 * Implements a class to render avatars in the app.
 */
class Avatar extends PureComponent {
    /**
     * Default values for {@code Avatar} component's properties.
     *
     * @static
     */
    static defaultProps = {
        defaultIcon: IconUser,
        dynamicColor: true
    };
    /**
     * Instantiates a new {@code Component}.
     *
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        const { _corsAvatarURLs, url, useCORS } = props;
        this.state = {
            avatarFailed: false,
            isUsingCORS: Boolean(useCORS) || Boolean(url && isCORSAvatarURL(url, _corsAvatarURLs))
        };
        this._onAvatarLoadError = this._onAvatarLoadError.bind(this);
    }
    /**
     * Implements {@code Component#componentDidUpdate}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        const { _corsAvatarURLs, _loadableAvatarUrl, url } = this.props;
        if (prevProps.url !== url || prevProps._loadableAvatarUrl !== _loadableAvatarUrl) {
            // URI changed, so we need to try to fetch it again.
            // Eslint doesn't like this statement, but based on the React doc, it's safe if it's
            // wrapped in a condition: https://reactjs.org/docs/react-component.html#componentdidupdate
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                avatarFailed: false,
                failedURL: undefined,
                isUsingCORS: Boolean(this.props.useCORS) || Boolean(url && isCORSAvatarURL(url, _corsAvatarURLs))
            });
        }
    }
    /**
     * Implements {@code Componenr#render}.
     *
     * @inheritdoc
     */
    render() {
        const { _customAvatarBackgrounds, _initialsBase, _loadableAvatarUrl, _loadableAvatarUrlUseCORS, className, colorBase, defaultIcon, dynamicColor, id, size, status, testId, url } = this.props;
        const { avatarFailed, failedURL, isUsingCORS } = this.state;
        const avatarProps = {
            className,
            color: undefined,
            id,
            initials: undefined,
            onAvatarLoadError: undefined,
            onAvatarLoadErrorParams: undefined,
            size,
            status,
            testId,
            url: undefined,
            useCORS: isUsingCORS
        };
        // An explicitly provided URI is higher priority than an avatar URL, and it is not
        // validated, so on failure we fall back to _loadableAvatarUrl.
        //
        // Upstream _loadableAvatarUrl is only ever set after a successful preload, so it was
        // safe to keep rendering it forever. Here it is not: with disableThirdPartyRequests
        // the participants middleware sets it straight from the JWT without preloading, and
        // _mapStateToProps falls back to the raw avatarURL. A dead URL therefore reaches this
        // component, and re-rendering the same failing <img> is what produced a permanent
        // broken-image icon for participants whose photo is missing from storage. Once it has
        // failed, drop it so StatelessAvatar renders initials instead.
        //
        // A member photo is drawn from the variant sized for this avatar (<hash>_sm/_md/_lg.png)
        // rather than the full upload. When that fails, failedURL names it and the original is
        // tried next; only when the original fails too does it fall to initials.
        const useReduxLoadableAvatarURL = avatarFailed || !url;
        const reduxURL = _loadableAvatarUrl === failedURL ? undefined : _loadableAvatarUrl;
        const sizedReduxURL = memberPhotoSizedUrl(reduxURL, size);
        const effectiveURL = useReduxLoadableAvatarURL
            ? sizedReduxURL && sizedReduxURL !== failedURL ? sizedReduxURL : reduxURL
            : url;
        if (effectiveURL) {
            avatarProps.onAvatarLoadError = this._onAvatarLoadError;
            if (useReduxLoadableAvatarURL) {
                avatarProps.onAvatarLoadErrorParams = { dontRetry: true, url: effectiveURL };
                avatarProps.useCORS = _loadableAvatarUrlUseCORS;
            }
            avatarProps.url = effectiveURL;
        }
        const initials = getInitials(_initialsBase);
        if (initials) {
            if (dynamicColor) {
                avatarProps.color = getAvatarColor(colorBase || _initialsBase, _customAvatarBackgrounds ?? []);
            }
            avatarProps.initials = initials;
        }
        if (navigator.product !== 'ReactNative') {
            avatarProps.iconUser = defaultIcon;
        }
        return (React.createElement(StatelessAvatar, { ...avatarProps }));
    }
    /**
     * Callback to handle the error while loading of the avatar URI.
     *
     * @param {Object} params - An object with parameters.
     * @param {boolean} params.dontRetry - If false we will retry to load the Avatar with different CORS mode.
     * @param {string} params.url - The URL that failed to load, so it is not rendered again.
     * @returns {void}
     */
    _onAvatarLoadError(params = {}) {
        const { dontRetry = false, url } = params;
        if (Boolean(this.props.useCORS) === this.state.isUsingCORS && !dontRetry) {
            // try different mode of loading the avatar.
            this.setState({
                isUsingCORS: !this.state.isUsingCORS
            });
        }
        else {
            // we already have tried loading the avatar with and without CORS and it failed.
            this.setState({
                avatarFailed: true,
                failedURL: url ?? this.props.url
            });
        }
    }
}
/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @param {IProps} ownProps - The own props of the component.
 * @returns {IProps}
 */
export function _mapStateToProps(state, ownProps) {
    const { colorBase, displayName, participantId } = ownProps;
    const _participant = participantId ? getParticipantById(state, participantId) : undefined;
    const _initialsBase = _participant?.name ?? displayName;
    const { corsAvatarURLs } = state['features/base/config'];
    // Use loadableAvatarUrl if available, otherwise fall back to the raw
    // avatarURL from the JWT/participant data. This ensures avatars from
    // KamoMeet's OTK→JWT flow display even if the preload/validation
    // pipeline didn't set loadableAvatarUrl (e.g. due to disableThirdPartyRequests).
    const avatarUrl = _participant?.loadableAvatarUrl || _participant?.avatarURL || undefined;
    return {
        _customAvatarBackgrounds: state['features/dynamic-branding'].avatarBackgrounds,
        _corsAvatarURLs: corsAvatarURLs,
        _initialsBase,
        _loadableAvatarUrl: avatarUrl,
        _loadableAvatarUrlUseCORS: _participant?.loadableAvatarUrlUseCORS ?? true,
        colorBase
    };
}
export default connect(_mapStateToProps)(Avatar);
